/** Wizard state for the integration creation flow.
 *
 * Split into `booking` / `fleet` branches: the two paths share almost no fields,
 * and each step reads/writes only its own branch. The `kind` discriminator picks
 * which branch (and which step list) is active.
 */
import type { IntegrationCreateInput } from '@/api/integrations';

export type IntegrationKind = 'booking' | 'fleet';
export type AccountMode = 'single' | 'multi';
export type FleetRegion = 'uk' | 'us' | 'fr';

export const FLEET_REGIONS: FleetRegion[] = ['uk', 'us', 'fr'];
export const VEHICLE_STATUSES = ['moving', 'stopped', 'idle', 'offline', 'decommissioned'];

/** The four restech-independent survey/invitation defaults (plan decision). */
export const SEND_DEFAULTS: Record<string, boolean> = {
  send_surveys_api: true,
  send_surveys_webhook: true,
  send_invitations_api: false,
  send_invitations_webhook: true,
};

export interface BookingWizardData {
  restech: string | null;
  account_mode: AccountMode;
  slug: string;
  display_name: string;
  source_id: string | null;
  /** Auth + other schema-driven fields (excludes source_id, merged at submit). */
  config: Record<string, unknown>;
  // Webhook step:
  /** api mode (Ventrata): install the LEGACY webhook. Off by default -- new tenants get OCTO
   * notification subscriptions instead, and legacy webhooks are being retired. */
  installWebhook: boolean;
  /** email mode (FareHarbor / TourCMS): open the webhook-setup e-mail after creation. Never sent automatically:
   * a sandbox never e-mails vendor support, and production only behind a platform switch. */
  openEmail: boolean;
}

export interface FleetWizardData {
  region: FleetRegion;
  customer_id: string;
  username: string;
  password: string;
  application_id: string;
  site_name: string;
  user: string;
  keep_location_history: boolean;
  unassign_if_not_in_api: boolean;
  hidden_vehicle_statuses: string[];
  route_unassign_events: string[];
  enabled: boolean;
}

export interface WizardData {
  kind: IntegrationKind;
  booking: BookingWizardData;
  fleet: FleetWizardData;
}

export const BOOKING_DEFAULTS: BookingWizardData = {
  restech: null,
  account_mode: 'single',
  slug: '',
  display_name: '',
  source_id: null,
  config: { ...SEND_DEFAULTS },
  installWebhook: false,
  openEmail: true,
};

export const FLEET_DEFAULTS: FleetWizardData = {
  region: 'uk',
  customer_id: '',
  username: '',
  password: '',
  application_id: '',
  site_name: '',
  user: '',
  keep_location_history: false,
  unassign_if_not_in_api: false,
  hidden_vehicle_statuses: [],
  route_unassign_events: [],
  enabled: true,
};

export const WIZARD_DEFAULTS: WizardData = {
  kind: 'booking',
  booking: { ...BOOKING_DEFAULTS, config: { ...SEND_DEFAULTS } },
  fleet: { ...FLEET_DEFAULTS },
};

/** Assemble the fleet `settings` blob, dropping empty optional keys. */
export function fleetSettings(f: FleetWizardData): Record<string, unknown> {
  const s: Record<string, unknown> = {
    region: f.region,
    customer_id: f.customer_id,
    username: f.username,
    password: f.password,
  };
  if (f.application_id) s.application_id = f.application_id;
  if (f.site_name) s.site_name = f.site_name;
  if (f.user) s.user = f.user;
  return s;
}

/** Build the POST body for the create endpoint from wizard state. */
export function buildCreateInput(data: WizardData): IntegrationCreateInput {
  if (data.kind === 'fleet') {
    const f = data.fleet;
    return {
      kind: 'fleet',
      fleet_mgmt: 'quartix',
      enabled: f.enabled,
      settings: fleetSettings(f),
      keep_location_history: f.keep_location_history,
      unassign_if_not_in_api: f.unassign_if_not_in_api,
      hidden_vehicle_statuses: f.hidden_vehicle_statuses,
      route_unassign_events: f.route_unassign_events,
    };
  }
  const b = data.booking;
  const config: Record<string, unknown> = { ...b.config };
  if (b.source_id) config.source_id = b.source_id;
  return {
    kind: 'booking',
    restech: b.restech ?? '',
    account_mode: b.account_mode,
    ...(b.account_mode === 'multi' ? { slug: b.slug } : {}),
    display_name: b.display_name || (b.account_mode === 'multi' ? b.slug : 'Default'),
    config,
  };
}
